import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";

const LOGO = "/assets/images/kiddo-logo.png";

/**
 * Full-screen overlay shown during the brief logout-and-redirect window so
 * the dashboard doesn't visibly flash empty/unauth state while the auth
 * context tears down + the router navigates back to "/".
 *
 * Visual parity with ``BrandedLoader``
 * ------------------------------------
 * Iter80 (Feb 2026) — the logout overlay used to have its own custom
 * animation (logo pulse + halo ring + 3-dot hop). The product ask was
 * for the logout animation to *exactly* mirror the login animation and
 * only differ in text. This file now reuses the same JSX structure as
 * ``BrandedLoader`` (ping ring + pulse ring + spinner + logo + bouncing
 * dots) and only swaps the copy. The two components remain separate
 * because their ARIA semantics differ — login is a modal "alertdialog"
 * (blocking, must-be-acknowledged), logout is a passive "status" (an
 * announcement that the app is signing you out).
 *
 * Usage
 * -----
 *   const [out, setOut] = useState(false);
 *   const onLogout = () => {
 *     setOut(true);
 *     // give the overlay one frame to paint, then tear down + redirect
 *     setTimeout(() => { logout(); navigate("/"); }, 600);
 *   };
 *   return (<>{out && <LogoutOverlay />} ... </>)
 */
export default function LogoutOverlay({ name }) {
  if (typeof document === "undefined") return null;
  const title = name ? `See you soon, ${name}!` : "Signing you out…";
  const subtitle = "We're saving your progress. Come back any time.";
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-cream/95 dark:bg-[#0F0B1A]/95 backdrop-blur-md animate-fade-in"
      role="status"
      aria-live="polite"
      aria-label="Signing you out"
      data-testid="logout-overlay"
    >
      <div className="text-center px-6 max-w-sm">
        {/* Same ring stack as BrandedLoader — ping (outermost), pulse
            (mid), spinner (inner), then the logo pinned in the center. */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <span className="absolute inset-0 rounded-full bg-teal-600/20 animate-ping" />
          <span className="absolute inset-2 rounded-full bg-teal-600/30 animate-pulse" />
          <Loader2 className="absolute inset-0 m-auto w-24 h-24 text-teal-600/40 animate-spin" strokeWidth={1.5} />
          <img src={LOGO} alt="Kiddo+" className="absolute inset-0 m-auto w-12 h-12 rounded-full shadow-soft" />
        </div>
        <h2 className="font-heading text-xl sm:text-2xl text-inkdark dark:text-cream animate-fade-in-up">
          {title}
        </h2>
        <p className="text-sm text-inkmid dark:text-inklight mt-2 animate-fade-in-up animation-delay-100">
          {subtitle}
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-5">
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce" />
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce animation-delay-150" />
          <span className="w-2 h-2 rounded-full bg-teal-600 animate-bounce animation-delay-300" />
        </div>
      </div>
    </div>,
    document.body
  );
}
